import { useEffect, useRef, useState } from "react";
import {
  fetchEngineData,
  fetchGeneratorEngineData,
  fetchMainEngineData,
  fetchShaftGeneratorEngineData,
} from "../../../api/baseEngineData";
import { calDurations } from "../../../constants/calDuration";
import {
  craneDataProcess,
  fetchCrane,
  fetchLeg,
  fetchThruster,
  fetchTrimHeel,
  legDataProcess,
  thrusterDataProcess,
  trimHeelProcess,
} from "../../../hooks/useSepTrendData";
import { FETCH_CANCEL_MESSAGE } from "../../../constants/constants";
import { SEP_CHART_TYPE } from "../../../constants/trendGraph/sepLines";
import ElectricWorker from "../../trendPage/electric/worker/electricGraphData.worker";
import * as Comlink from "comlink";
import { timeKey } from "../../../constants/timeKey";
import { logger } from "../../../api/logger";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { fetchVesselGpvtgData } from "../../../api/navigationData";
import { fetchChannelDataByChartId } from "../../../api/channel";
import { convertPositionTextToNumeric } from "../../../helpers/enginePosition";
import { ELECTRIC_TREND_TYPE } from "../../../constants/trendGraph/electricLines";
import { getTrendData } from "../../trendPage/electric/useElectricDataList";
import { customTrendDataProcess } from "../../../util/trendDataProcess/custamTrendDataProcess";
import { engineTrendDataProcess } from "../../../util/trendDataProcess/engineTrendDataProcess";

export const DATA_FETCH_TYPE = {
  ENGINE: "engine",
  SEP: "sep",
  GENERATOR: "generator",
  CUSTOM: "custom",
};

export const getDataFetchType = (selectedGraphType) => {
  switch(selectedGraphType) {
    case GRAPH_TYPE.ELECTRIC: return DATA_FETCH_TYPE.GENERATOR;
    case GRAPH_TYPE.SEP: return DATA_FETCH_TYPE.SEP;
    case GRAPH_TYPE.ENGINE: return DATA_FETCH_TYPE.ENGINE;
    case GRAPH_TYPE.CUSTOM: return DATA_FETCH_TYPE.CUSTOM;
    default: return
  }
};

export const createGeneratorData = async ({
  sgData,
  geData,
  unmount,
  setGraphData,
  setIsLoading,
}) => {
  const worker = ElectricWorker();
  const wrap = Comlink.wrap(worker);
  const newData = await wrap?.createGraphData({
    sgData,
    geData,
  });
  const key = timeKey(newData);
  if(!unmount?.current){
    const nextData = newData.sort((a, b) => new Date(a[key]).getTime() - new Date(b[key]).getTime());
    setGraphData(nextData.length > 0 ? nextData : []);
  }
  setIsLoading(false);
  worker.terminate();
};

const channelModCancelTokens = [undefined, undefined];

export const comparisonDataFetch = async (props) => {
  const {
    vesselId,
    dataFetchType,
    selectedGraph,
    setGraphData,
    setGraphConfig,
    setDigitalData,
    startDate,
    endDate,
    unmount,
    position,
    currentVesselSelector,
    setIsLoading,
    number,
  } = props;
  const interval = calDurations({ startDate, endDate });

  if (typeof channelModCancelTokens[number] !== typeof undefined) {
    channelModCancelTokens[number].cancel(FETCH_CANCEL_MESSAGE);
  }

  switch (dataFetchType) {
    case DATA_FETCH_TYPE.ENGINE:
      Promise.all([
        fetchEngineData(vesselId, startDate, endDate, interval),
        fetchMainEngineData(
          convertPositionTextToNumeric(position),
          vesselId,
          startDate,
          endDate,
          interval,
        ),
        fetchVesselGpvtgData(vesselId, startDate, endDate, interval),
      ])
        .then((d) => {
          engineTrendDataProcess(d, selectedGraph, position, currentVesselSelector, startDate, endDate).then((dd) => {
            setGraphData(dd);
            setIsLoading(false)
          })
        })
        .catch((e) => {});
      break;
    case DATA_FETCH_TYPE.SEP:
      switch (selectedGraph) {
        case SEP_CHART_TYPE.LEGS:
          fetchLeg(
            vesselId,
            startDate,
            endDate,
            interval,
            currentVesselSelector?.NoLeg,
          )
            .then((d) => legDataProcess(d ?? [], unmount, setGraphData))
            .then(() => setIsLoading(false))
            .catch((e) => {});
          break;
        case SEP_CHART_TYPE.CRANE1:
          fetchCrane(
            vesselId,
            startDate,
            endDate,
            interval,
            currentVesselSelector?.NoCrane,
          )
            .then((d) => craneDataProcess(d ?? [], setGraphData, 1))
            .then(() => setIsLoading(false))
            .catch((e) => {});
          break;
        case SEP_CHART_TYPE.CRANE2:
          fetchCrane(
            vesselId,
            startDate,
            endDate,
            interval,
            currentVesselSelector?.NoCrane,
          )
            .then((d) => craneDataProcess(d ?? [], setGraphData, 2))
            .then(() => setIsLoading(false))
            .catch((e) => {});
          break;
        case SEP_CHART_TYPE.THRUSTER:
          fetchThruster(
            vesselId,
            startDate,
            endDate,
            interval,
            currentVesselSelector?.NoThruster,
          )
            .then((d) => thrusterDataProcess(d ?? [], unmount, setGraphData))
            .then(() => setIsLoading(false))
            .catch((e) => {});
          break;
        case SEP_CHART_TYPE.TRIM_AND_HEEL:
          fetchTrimHeel(vesselId, startDate, endDate, interval)
            .then((d) => trimHeelProcess(d ?? [], setGraphData))
            .then(() => setIsLoading(false))
            .catch((e) => {});
          break;
      }
      break;
    case DATA_FETCH_TYPE.GENERATOR: {
      if(selectedGraph === ELECTRIC_TREND_TYPE.ELECTRIC_POWER_TREND) {
        Promise.all([
            fetchGeneratorEngineData({
              vesselId,
              startDate,
              endDate,
              interval,
            }),
            fetchShaftGeneratorEngineData({
              vesselId,
              startDate,
              endDate,
              interval,
            })
        ])
        .then((d) => {
          createGeneratorData({
            sgData: currentVesselSelector?.NoSG < 1 ? [] : d[1]?.loadingData,
            geData: d[0]?.loadingData,
            unmount,
            setGraphData,
            setIsLoading,
          });
        })
        .catch((e) => {});
        break;
      }else{
        getTrendData(vesselId, selectedGraph, startDate, endDate, setIsLoading).then((data) => {
          setGraphData(data.data)
          setGraphConfig({...data.config, ...data.commonNames});
        }).catch((e) => {});
        break;
      }
    }
    case DATA_FETCH_TYPE.CUSTOM:
      fetchChannelDataByChartId(vesselId, selectedGraph, startDate, endDate, interval).then((data) => {
        const { returnData, returnDataDigital } = customTrendDataProcess(data, setGraphData, setDigitalData, setIsLoading);
        setGraphData(returnData);
        setDigitalData(returnDataDigital);
        setIsLoading(false);
      })
      break;
    default:
      Error("NO DATA!!");
  }
};

export const useComparisonDataFetch = (params) => {
  const {
    vessel,
    vesselId,
    selectedGraph,
    position,
    startDate,
    endDate,
    selectedGraphType,
  } = params;
  const [graphData, setGraphData] = useState([]);
  const [graphConfig, setGraphConfig] = useState(null);
  const [digitalData, setDigitalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const unmount = useRef(false);
  const currentVesselInfo = vessel;
  const dataFetchType = getDataFetchType(selectedGraphType);
  const comparisonDataFetchFunction = async () => {
    try {
      setIsLoading(true);
      unmount.current ||
        (await comparisonDataFetch({
          ...params,
          setGraphData,
          setGraphConfig,
          setDigitalData,
          unmount,
          currentVesselSelector: currentVesselInfo,
          setIsLoading,
          dataFetchType,
        }));
    } catch (e) {
      logger.error(e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setIsOnline(false);
    setGraphData([]);
    setGraphConfig(null);
    setDigitalData([]);

    if (!vesselId || startDate === "" || endDate === "" || !selectedGraph) return;
    comparisonDataFetchFunction();
  }, [
    vesselId,
    selectedGraph,
    position,
    startDate,
    endDate,
  ]);
  return {
    graphData,
    graphConfig,
    isLoading,
    isOnline,
    digitalData,
  };
};
